import React, { useState, useEffect, useRef } from "react";
import { ImportProductsToQuoteResponse } from "@/Contracts/ProposalBuilder/ImportProducts";
import ZDrawer from "@molecules/ZDrawer";
import { ZIcon, ZTag, ZTypography } from "@atoms";
import { EIcons, EIllustrations } from "@atoms/ZIcon/interface";
import ZImportProductsToQuoteDrawerPreview from "./ZImportProductsToQuoteDrawerPreview";
import ZMessage from "@molecules/ZMessage";
import "./index.less";
import { EColors } from "@atoms/ZColors/interface";
import { IZImportProductsToQuoteDrawerProps, EFileType } from "./interface";
import { EFontWeights } from "@atoms/ZTypography/interface";
import Divider from "antd/lib/divider";
import ZImportFooter from "./ZImportFooter";
import { EFooterVariant } from "./ZImportFooter/interface";
import { useFeatureFlag } from "@/Hooks/useFeature";
import ZPricingConfigurationDrawer from "./ZPricingConfigurationDrawer";
import { PricingConfig } from "./ZPricingConfigurationDrawer/interface";
import {
	importUserDefinedProductsToQuote,
	downloadBulkImportTemplate
} from "@/ProposalBuilder/services/ImportProductsToQuote";
import { extension } from "mime-types";

const ZImportProductsToQuoteDrawer: React.FC<
	IZImportProductsToQuoteDrawerProps
> = ({
	visible,
	onClose,
	blockId,
	onImportConfirmed,
	importProductsToQuote
}) => {
	const isImportProductsEnabled = useFeatureFlag("import_products_to_quote");
	const [selectedType, setSelectedType] = useState<EFileType>(EFileType.PDF);
	const [fileName, setFileName] = useState("");
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [showPricingConfigDrawer, setShowPricingConfigDrawer] =
		useState(false);
	const [showSecondDrawer, setShowSecondDrawer] = useState(false);
	const [showPreviewDrawer, setShowPreviewDrawer] = useState(false);
	const [importResponse, setImportResponse] =
		useState<ImportProductsToQuoteResponse | null>(null);
	const [pricingConfig, setPricingConfig] = useState<PricingConfig>({
		targetMargin: "",
		markup: "",
		pricingMode: "margin",
		lineItemDiscount: "",
		userPreferences: "",
		additionalImages: []
	});
	const [isImporting, setIsImporting] = useState(false);
	const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
	const [isAiImport, setIsAiImport] = useState(true);
	const isImportInProgressRef = useRef(false);
	const latestReqId = useRef(0);

	// ---------- Effects ----------
	useEffect(() => {
		if (!visible) {
			setFileName("");
			setSelectedFile(null);
			setSelectedType(EFileType.PDF);
			setImportResponse(null);
			setShowPricingConfigDrawer(false);
			setShowSecondDrawer(false);
			setShowPreviewDrawer(false);
			setPricingConfig({
				targetMargin: "",
				markup: "",
				pricingMode: "margin",
				lineItemDiscount: "",
				userPreferences: "",
				additionalImages: []
			});
			setIsImporting(false);
			setIsAiImport(true);
			isImportInProgressRef.current = false;
		}
	}, [visible]);

	// ---------- File Validation ----------
	const validateFileType = (
		file: File,
		fileType: EFileType
	): { isValid: boolean; message: string } => {
		const fileName = file.name.toLowerCase();
		const mimeType = file.type.toLowerCase();

		if (fileType === EFileType.XLS) {
			const validExtensions = [".csv", ".xlsx"];
			const validMimeTypes = [
				"text/csv",
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				"application/vnd.ms-excel"
			];

			const hasValidExtension = validExtensions.some(ext =>
				fileName.endsWith(ext)
			);
			const hasValidMimeType = validMimeTypes.includes(mimeType);

			if (!hasValidExtension && !hasValidMimeType) {
				return {
					isValid: false,
					message:
						"Please upload a valid CSV or Excel file (.csv, .xlsx)"
				};
			}
		} else {
			const validExtensions = [
				".pdf",
				".docx",
				".pptx",
				".xlsx",
				".csv",
				".txt",
				".jpg",
				".jpeg",
				".png"
			];
			const hasValidExtension = validExtensions.some(ext =>
				fileName.endsWith(ext)
			);

			if (!hasValidExtension) {
				return {
					isValid: false,
					message:
						"Please upload a supported file type (PDF, DOCX, PPTX, XLSX, CSV, TXT, JPG, PNG)"
				};
			}
		}

		return { isValid: true, message: "" };
	};

	// ---------- Handlers ----------
	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			const validationResult = validateFileType(file, selectedType);
			if (!validationResult.isValid) {
				ZMessage.error(validationResult.message);
				e.target.value = "";
				return;
			}

			const maxSize = 50 * 1024 * 1024;
			if (file.size > maxSize) {
				ZMessage.error("File size must be less than 50MB");
				e.target.value = "";
				return;
			}

			setFileName(file.name);
			setSelectedFile(file);
		} else {
			setFileName("");
			setSelectedFile(null);
		}
	};

	const handleRemoveFile = () => {
		setFileName("");
		setSelectedFile(null);
	};

	const handleDownloadTemplate = async (fileType: "csv" | "xls") => {
		try {
			setIsDownloadingTemplate(true);
			const blob = await downloadBulkImportTemplate(fileType);
			const ext = extension(blob.type) || fileType;
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `Pricing_Table_Products_Bulk_Import_Template.${ext}`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Error downloading template:", error);
			ZMessage.error("Failed to download template. Please try again.");
		} finally {
			setIsDownloadingTemplate(false);
		}
	};

	const handleCancel = () => {
		setFileName("");
		setSelectedFile(null);
		setSelectedType(EFileType.PDF);
		setImportResponse(null);
		setShowPricingConfigDrawer(false);
		setShowSecondDrawer(false);
		setShowPreviewDrawer(false);
		setPricingConfig({
			targetMargin: "",
			markup: "",
			pricingMode: "margin",
			lineItemDiscount: "",
			userPreferences: "",
			additionalImages: []
		});
		setIsImporting(false);
		isImportInProgressRef.current = false;
		onClose?.();
	};

	const handleProceed = () => {
		if (!selectedFile) {
			ZMessage.error("Please select a file to import");
			return;
		}

		if (selectedType === EFileType.XLS) {
			handleUserDefinedImport();
		} else {
			setShowPricingConfigDrawer(true);
		}
	};

	const handlePricingBack = () => {
		setShowPricingConfigDrawer(false);
	};

	const handlePricingSkip = () => {
		const defaultConfig: PricingConfig = {
			targetMargin: "0",
			markup: "",
			pricingMode: "margin",
			lineItemDiscount: "0",
			userPreferences: "",
			additionalImages: []
		};
		handleImportWithPricing(defaultConfig);
	};

	const handlePricingConfirm = (config: PricingConfig) => {
		setPricingConfig(config);
		handleImportWithPricing(config);
	};

	const handleImportWithPricing = async (config: PricingConfig) => {
		if (!selectedFile)
			return ZMessage.error("Please select a file to import");
		if (!blockId) return ZMessage.error("Block ID is required");
		if (isImportInProgressRef.current) return;

		try {
			isImportInProgressRef.current = true;
			setIsImporting(true);
			setIsAiImport(true);
			setShowPricingConfigDrawer(false);
			setShowSecondDrawer(true);

			const reqId = ++latestReqId.current;

			const data = await importProductsToQuote(
				blockId,
				selectedFile,
				config.lineItemDiscount || "0",
				config.pricingMode === "markup"
					? "0"
					: config.targetMargin || "0",
				config.userPreferences || "",
				config.additionalImages || [],
				config.pricingMode === "markup"
					? config.markup || "0"
					: undefined
			);

			if (reqId !== latestReqId.current) return;

			setImportResponse(data);
			setShowSecondDrawer(false);
			setShowPreviewDrawer(true);
		} catch (err: any) {
			console.error("Error importing products:", err);
			setShowSecondDrawer(false);
			ZMessage.error(
				err instanceof Error
					? `Failed to import products: ${err.message}`
					: "Failed to import products. Please try again."
			);
		} finally {
			isImportInProgressRef.current = false;
			setIsImporting(false);
		}
	};

	const handleUserDefinedImport = async () => {
		if (!selectedFile)
			return ZMessage.error("Please select a file to import");
		if (!blockId) return ZMessage.error("Block ID is required");
		if (isImportInProgressRef.current) return;

		try {
			isImportInProgressRef.current = true;
			setIsImporting(true);
			setIsAiImport(false);
			setShowSecondDrawer(true);

			const reqId = ++latestReqId.current;

			const bulkImportResponse = await importUserDefinedProductsToQuote(
				blockId,
				selectedFile
			);

			if (reqId !== latestReqId.current) return;

			const hasErrors =
				bulkImportResponse.errors &&
				bulkImportResponse.errors.length > 0;
			const hasBlock = !!bulkImportResponse.updatedBlockOpt;

			const sheetImportBlockedErrors =
				bulkImportResponse.errors?.filter(
					err => err.severity === "sheet_import_blocked"
				) || [];
			const hasSheetImportBlockedErrors =
				sheetImportBlockedErrors.length > 0;

			if (!hasBlock) {
				setShowSecondDrawer(false);
				if (hasSheetImportBlockedErrors) {
					const errorMessages = sheetImportBlockedErrors
						.map(err => err.message)
						.join("\n");
					ZMessage.error(
						`Failed to import products:\n${errorMessages}${
							bulkImportResponse.rowsSkippedCount > 0
								? `\n(${bulkImportResponse.rowsSkippedCount} rows skipped)`
								: ""
						}`
					);
				} else {
					ZMessage.error(
						"Import failed: No products were imported. Please check your file format."
					);
				}
				return;
			}

			if (hasSheetImportBlockedErrors) {
				const errorMessages = sheetImportBlockedErrors
					.map(err => err.message)
					.join(", ");
				ZMessage.warning(
					`Import completed with warnings: ${errorMessages}${
						bulkImportResponse.rowsSkippedCount > 0
							? ` (${bulkImportResponse.rowsSkippedCount} rows skipped)`
							: ""
					}`
				);
			}

			const transformedResponse: ImportProductsToQuoteResponse = {
				updated_block: bulkImportResponse.updatedBlockOpt ?? null,
				document_type: null,
				customer_name: null,
				vendor_name: null,
				currency: null,
				date: null,
				due_date: null,
				payment_terms: null,
				data_details: `User-defined products imported successfully${
					bulkImportResponse.rowsSkippedCount > 0
						? ` (${bulkImportResponse.rowsSkippedCount} rows skipped)`
						: ""
				}`,
				errors: bulkImportResponse.errors,
				rowsSkippedCount: bulkImportResponse.rowsSkippedCount
			};

			setImportResponse(transformedResponse);
			setShowSecondDrawer(false);
			setShowPreviewDrawer(true);
		} catch (err: any) {
			console.error("Error importing user-defined products:", err);
			setShowSecondDrawer(false);
			ZMessage.error(
				err instanceof Error
					? `Failed to import products: ${err.message}`
					: "Failed to import products. Please try again."
			);
		} finally {
			isImportInProgressRef.current = false;
			setIsImporting(false);
		}
	};

	const handlePreviewDrawerClose = () => {
		setShowPreviewDrawer(false);
		setImportResponse(null);
		setFileName("");
		setSelectedFile(null);
		onClose?.();
	};

	const handleImportToQuote = (sectionName: string) => {
		const updatedBlock = importResponse?.updated_block;
		if (!updatedBlock) {
			ZMessage.error("No updated quote data found");
			return;
		}

		onImportConfirmed?.(updatedBlock, sectionName);

		setShowPreviewDrawer(false);
		setImportResponse(null);
		ZMessage.success("Products imported successfully to quote!");
	};

	// ---------- Footer ----------
	const footer = (
		<ZImportFooter
			variant={EFooterVariant.IMPORT_ZBUTTON}
			buttonText="Proceed"
			onClick={handleProceed}
			disabled={!selectedFile || isImporting}
			isLoading={isImporting}
			showAiDisclaimer={selectedType === EFileType.PDF}
			iconName={EIcons.STARICON}
			iconProps={{ colorVariant: 1 }}
			className="z-import-button"
		/>
	);

	// ---------- Render ----------
	// Early return if feature flag is disabled or all drawers are closed
	if (
		!isImportProductsEnabled ||
		(!visible &&
			!showPricingConfigDrawer &&
			!showSecondDrawer &&
			!showPreviewDrawer)
	)
		return null;

	return (
		<>
			{/* FIRST (MAIN) DRAWER — restored full UI */}
			<ZDrawer
				visible={visible}
				title={null}
				onClose={handleCancel}
				footer={footer}
				footerStyle={{ border: "none" }}
				className="z-import-products-to-quote-drawer"
				bodyStyle={{ padding: 0 }}
				closable={false}
				maskClosable={false}
				headerStyle={{ display: "none" }}
				destroyOnClose
				keyboard={false}
			>
				<div className="h-full flex flex-col relative">
					{/* Gradient header */}
					<div
						className="relative px-6 pb-0"
						style={{
							background:
								"radial-gradient(ellipse at center top, rgba(171, 255, 180, 0.4) 0%, rgba(171, 255, 180, 0.25) 40%, rgba(171, 255, 180, 0.08) 70%, transparent 85%), radial-gradient(ellipse 120% 140% at top left, rgba(204, 249, 255, 0.15) 0%, rgba(204, 249, 255, 0.08) 35%, transparent 70%), radial-gradient(ellipse 120% 140% at top right, rgba(107, 255, 235, 0.15) 0%, rgba(107, 255, 235, 0.08) 35%, transparent 70%), linear-gradient(to bottom, transparent 0%, white 100%)"
						}}
					>
						{/* Close btn */}
						<button
							onClick={handleCancel}
							className="preview-close-button"
							aria-label="Close"
						>
							<ZIcon name={EIcons.CLOSE} size="md" fill={true} />
						</button>

						{/* Header Content */}
						<div className="flex flex-col items-center gap-2 mt-5 mb-4">
							<ZIcon
								isIllustration
								name={EIllustrations.IMPORTPRODUCTTOQUOTEHEADER}
							/>
							<ZTypography.ZTitle
								level={3}
								className="text-2 font-semibold text-gray-9 m-0"
							>
								Import Products into Your Quote
							</ZTypography.ZTitle>
							<ZTypography.ZText className="text-1 text-gray-9 text-center leading-snug max-w-[90%]">
								Quickly upload items from quotes, order forms,
								invoices — no manual entry needed.
							</ZTypography.ZText>
						</div>

						{/* Step 1: file type */}
						<div>
							<ZTypography.ZText className="font-medium text-1 text-gray-9 mb-2 text-left block">
								Step 1: Select File type you want to import
							</ZTypography.ZText>

							<div className="flex flex-col gap-3">
								{/* PDF Button */}
								<div
									className={`z-file-type-container z-file-type-container-pdf relative ${
										selectedType === EFileType.PDF
											? "z-ai-import-badge"
											: "z-file-type-container-default"
									}`}
								>
									<ZTag
										color={EColors.BLUE}
										className="absolute top-2 right-2 z-10"
									>
										BETA
									</ZTag>
									<button
										className="z-file-type-button h-22 w-full"
										style={
											selectedType === EFileType.PDF
												? {
														background:
															"transparent",
														border: "none"
												  }
												: { backgroundColor: "#ffffff" }
										}
										onClick={() =>
											setSelectedType(EFileType.PDF)
										}
									>
										<ZIcon
											isIllustration
											name={
												EIllustrations.IMPORTPRODUCTTOQUOTEUSINGPDF
											}
											className="mr-3 ml-0"
										/>

										<div className="flex flex-col">
											<ZTypography.ZText className="font-medium text-1 text-gray-9">
												AI import
											</ZTypography.ZText>

											<ZTypography.ZText
												level={0.5}
												colorVariant={8}
												className="mt-0 whitespace-nowrap text-xs"
											>
												Import quotes, order forms,
												invoices, or vendor documents
												from your distributor or vendor.
											</ZTypography.ZText>
											<ZTypography.ZText
												level={0.5}
												colorVariant={8}
												className="mt-0 whitespace-nowrap text-xs"
											>
												Supports: PDF, DOCX, PPTX, XLSX,
												CSV, TXT, JPG, PNG (Yes —
												screenshots too!)
											</ZTypography.ZText>
										</div>
									</button>
								</div>

								{/* XLS/CSV Button */}
								<div
									className={`z-file-type-container z-file-type-container-xlsx relative ${
										selectedType === EFileType.XLS
											? "z-ai-import-badge"
											: "z-file-type-container-default"
									}`}
								>
									<button
										className="z-file-type-button h-22 w-full"
										style={
											selectedType === EFileType.XLS
												? {
														background:
															"transparent",
														border: "none"
												  }
												: { backgroundColor: "#ffffff" }
										}
										onClick={() =>
											setSelectedType(EFileType.XLS)
										}
									>
										<ZIcon
											isIllustration
											name={
												EIllustrations.IMPORTPRODUCTTOQUOTEUSINGEXCEL
											}
											className="mr-3 ml-0"
										/>

										<div className="flex flex-col">
											<div className="flex flex-col gap-1">
												<ZTypography.ZText
													className={`font-medium text-1 ${
														selectedType ===
														EFileType.XLS
															? "text-green-9"
															: "text-gray-9"
													}`}
												>
													User-Defined Import (XLSX /
													CSV)
												</ZTypography.ZText>
											</div>

											<ZTypography.ZText
												level={0.5}
												colorVariant={8}
												className="mt-0 whitespace-nowrap text-xs"
											>
												Import user defined quote
												(XLSX/CSV)
											</ZTypography.ZText>
											<ZTypography.ZText
												level={0.5}
												colorVariant={8}
												className="mt-0 whitespace-nowrap text-xs"
											>
												Supports: XLSX, CSV
											</ZTypography.ZText>
										</div>
									</button>
								</div>
							</div>
						</div>
					</div>

					<Divider className="my-3 mx-6" />
					{/* Step 2: guide + upload */}
					<div className="mb-2 px-6">
						<ZTypography.ZText
							weight={EFontWeights.MEDIUM}
							className="mb-3 block"
						>
							Step 2: Upload Your File
						</ZTypography.ZText>

						{/* Guide box container */}
						<div className="rounded-md border border-gray-4 border-solid p-3 mb-3">
							{selectedType === EFileType.XLS ? (
								<>
									<ZTypography.ZText
										level={0}
										className="mb-2"
									>
										Guide for the Spreadsheet to upload
										<br />
										1. Download and use our template file to
										upload and see how it works.
									</ZTypography.ZText>
									<div className="flex flex-col gap-3 mt-2 items-start">
										<button
											onClick={() =>
												handleDownloadTemplate("xls")
											}
											disabled={isDownloadingTemplate}
											className="flex items-center gap-2 text-blue-6 hover:text-blue-7 cursor-pointer no-underline bg-transparent border-none p-0"
										>
											<ZTypography.ZText
												color={EColors.BLUE}
												colorVariant={6}
											>
												{isDownloadingTemplate
													? "Downloading..."
													: "Download Template (XLSX)"}
											</ZTypography.ZText>
										</button>

										<button
											onClick={() =>
												handleDownloadTemplate("csv")
											}
											disabled={isDownloadingTemplate}
											className="flex items-center gap-2 text-blue-6 hover:text-blue-7 cursor-pointer no-underline bg-transparent border-none p-0"
										>
											<ZTypography.ZText
												color={EColors.BLUE}
												colorVariant={6}
											>
												{isDownloadingTemplate
													? "Downloading..."
													: "Download Template (CSV)"}
											</ZTypography.ZText>
										</button>
									</div>

									<ZTypography.ZText
										level={0}
										className="mb-0 mt-3"
									>
										2. Certain fields need to be entered in
										order for your import to be successful.
										Make sure they are complete before you
										start importing.
									</ZTypography.ZText>
								</>
							) : (
								<>
									<ZTypography.ZText
										level={0}
										className="mb-2"
									>
										This option works best with quotes,
										order forms, contracts, or invoices from
										vendors, manufacturers, or distributors.
									</ZTypography.ZText>
									<div className="flex items-center gap-1 mb-2">
										<ZTypography.ZText level={0}>
											Supported file types: PDF, XLSX,
											DOCX, CSV, JPG, PNG, and more.
										</ZTypography.ZText>
										<a
											href="/SampleQuote.pdf"
											target="_blank"
											rel="noopener noreferrer"
											className="text-blue-6 hover:text-blue-7 no-underline"
										>
											<ZTypography.ZText
												color={EColors.BLUE}
												colorVariant={6}
												level={0}
											>
												Quote PDF Sample
											</ZTypography.ZText>
										</a>
									</div>
									<div className="flex flex-col gap-2 mt-2">
										<ZTypography.ZText
											level={0}
											className="font-bold"
										>
											If a downloadable file isn't
											available:
										</ZTypography.ZText>
										<div className="flex gap-2 items-start">
											<ZTypography.ZText
												level={0}
												className="mt-0"
											>
												•
											</ZTypography.ZText>
											<ZTypography.ZText
												level={0}
												className="flex-1"
											>
												Take a screenshot from the
												vendor portal, or
											</ZTypography.ZText>
										</div>
										<div className="flex gap-2 items-start">
											<ZTypography.ZText
												level={0}
												className="mt-0"
											>
												•
											</ZTypography.ZText>
											<ZTypography.ZText
												level={0}
												className="flex-1"
											>
												Use a photograph of a printed
												quote or order form
											</ZTypography.ZText>
										</div>
										<ZTypography.ZText
											level={0}
											className="mb-0"
										>
											Once uploaded, AI will parse the
											file and convert it to a Zomentum
											Quote. This may take up to 60
											seconds, so please do not go back or
											refresh during processing.
										</ZTypography.ZText>
									</div>
								</>
							)}
						</div>

						{/* Upload area (same UI) */}
						<div className="border-gray-4 border border-solid bg-gray-2 h-20">
							{!fileName ? (
								<label className="flex flex-col items-center justify-center border border-gray-4 border-solid gap-3 cursor-pointer h-full">
									<input
										type="file"
										className="hidden"
										onChange={handleFileChange}
										accept={
											selectedType === EFileType.XLS
												? ".csv,.xlsx"
												: ".pdf,.docx,.pptx,.xlsx,.csv,.txt,.jpg,.jpeg,.png"
										}
									/>
									<div className="flex items-center gap-1">
										<ZIcon
											name={EIcons.UPLOAD}
											size="sm"
											color={EColors.BLUE}
											colorVariant={6}
										/>
										<ZTypography.ZText
											colorVariant={6}
											color={EColors.BLUE}
										>
											Upload
										</ZTypography.ZText>
									</div>

									{/* Supported formats text */}
									<ZTypography.ZText
										level={0.5}
										colorVariant={8}
									>
										{selectedType === EFileType.XLS
											? "Only Excel (.xlsx) and .csv file types are supported."
											: "Supports: PDF, DOCX, PPTX, XLSX, CSV, TXT, JPG, PNG (Yes — screenshots too!)"}
									</ZTypography.ZText>
								</label>
							) : (
								<div className="flex flex-col items-center justify-center h-full gap-2">
									<div className="flex items-center gap-2">
										<div className="flex items-center gap-2">
											<ZIcon
												name={EIcons.CHECKCIRCLESOLID}
												size="sm"
												color={EColors.BLUE}
												colorVariant={6}
											/>
											<ZTypography.ZText
												color={EColors.BLUE}
												colorVariant={6}
												weight={EFontWeights.SEMIBOLD}
											>
												Upload completed
											</ZTypography.ZText>
										</div>

										<div className="flex items-center gap-2 p-1 bg-blue-1">
											<ZIcon
												name={
													EIcons.FILEATTACHMENTUPLOADED
												}
												size="sm"
												colorVariant={7}
											/>
											<ZTypography.ZText
												colorVariant={6}
												color={EColors.BLUE}
											>
												{fileName}
											</ZTypography.ZText>
											<ZIcon
												name={EIcons.CLOSE}
												colorVariant={7}
												className="hover:text-gray-9 cursor-pointer"
												onClick={handleRemoveFile}
												fill={true}
											/>
										</div>
									</div>

									<ZTypography.ZText colorVariant={8}>
										Please click 'proceed' to finish
										importing your data
									</ZTypography.ZText>
								</div>
							)}
						</div>
					</div>
				</div>
			</ZDrawer>

			{/* PRICING CONFIGURATION DRAWER */}
			<ZPricingConfigurationDrawer
				visible={showPricingConfigDrawer}
				onClose={handlePricingBack}
				onSkip={handlePricingSkip}
				onConfirm={handlePricingConfirm}
				initialValues={pricingConfig}
			/>

			{/* SECOND DRAWER (Parsing) */}
			<ZDrawer
				visible={showSecondDrawer}
				title={null}
				className="z-import-products-to-quote-drawer-secondary"
				closable={false}
				headerStyle={{ display: "none" }}
				destroyOnClose
				maskClosable={false}
				keyboard={false}
			>
				<div className="h-full w-full bg-gray-1 flex flex-col">
					{/* Main content area - centered */}
					<div className="flex-1 flex flex-col gap-2 items-center justify-center px-8">
						<ZTypography.ZText
							weight={EFontWeights.SEMIBOLD}
							colorVariant={6}
							color={EColors.BLUE}
							className="inline-flex items-center animate-pulse"
						>
							<ZIcon
								name={EIcons.AIPARSING}
								size="md"
								colorVariant={6}
								color={EColors.BLUE}
							/>
							{selectedType === EFileType.XLS
								? "Processing your product import..."
								: "Parsing your Quotes with AI intelligence..."}
						</ZTypography.ZText>

						<ZTypography.ZText>
							{selectedType === EFileType.XLS
								? "We're importing your products, this may take a few seconds."
								: "We're reading product details, prices, and quantities, this may take a few seconds."}
						</ZTypography.ZText>
					</div>

					{/* Footer section */}
					<div className="my-4 flex items-center justify-center">
						<ZTypography.ZText colorVariant={7} level={0.5}>
							Please don't press back or refresh
						</ZTypography.ZText>
					</div>
				</div>
			</ZDrawer>

			{/* PREVIEW DRAWER */}
			<ZDrawer
				visible={showPreviewDrawer}
				title={null}
				onClose={handlePreviewDrawerClose}
				className="z-import-product-preview"
				bodyStyle={{ padding: 0 }}
				closable={false}
				headerStyle={{ display: "none" }}
				destroyOnClose
				maskClosable={false}
				keyboard={false}
			>
				{importResponse ? (
					<ZImportProductsToQuoteDrawerPreview
						data={importResponse}
						onImportConfirmed={handleImportToQuote}
						onClose={handlePreviewDrawerClose}
						isLoading={false}
						isAiImport={isAiImport}
					/>
				) : (
					<div className="p-6 text-center">
						<ZTypography.ZText>
							Loading preview data...
						</ZTypography.ZText>
					</div>
				)}
			</ZDrawer>
		</>
	);
};

export default ZImportProductsToQuoteDrawer;
